import { useEffect, useState, useCallback, useRef } from "react";
import { useDebouncedValue } from "../lib/useDebounce";
import { Link } from "react-router-dom";
import { Search, BarChart3, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { RFQReport } from "./RFQReport";

export const RFQ_STATUSES = ["NEW", "REVIEW", "SOURCING", "SUPPLIER_QUOTES_PENDING", "ANALYSIS", "CUSTOMER_QUOTE", "NEGOTIATION", "WON", "LOST", "PURCHASE_ORDER", "FULFILLMENT", "SHIPPING", "DELIVERED", "CLOSED"];
const RFQ_PRIORITIES = ["LOW", "NORMAL", "HIGH", "URGENT"];

const STATUS_COLORS = {
  NEW: "bg-blue-100 text-blue-700", REVIEW: "bg-amber-100 text-amber-700",
  SOURCING: "bg-purple-100 text-purple-700", SUPPLIER_QUOTES_PENDING: "bg-orange-100 text-orange-700",
  ANALYSIS: "bg-cyan-100 text-cyan-700", CUSTOMER_QUOTE: "bg-indigo-100 text-indigo-700",
  NEGOTIATION: "bg-fuchsia-100 text-fuchsia-700", WON: "bg-green-100 text-green-700",
  LOST: "bg-red-100 text-red-700", PURCHASE_ORDER: "bg-teal-100 text-teal-700",
  FULFILLMENT: "bg-lime-100 text-lime-700", SHIPPING: "bg-sky-100 text-sky-700",
  DELIVERED: "bg-emerald-100 text-emerald-700", CLOSED: "bg-slate-200 text-slate-600",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export default function RFQList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [view, setView] = useState("list");

  const [assigned, setAssigned] = useState("");
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [rtype, setRtype] = useState("");
  useEffect(() => { api.get("/admin/staff/lite").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const dq = useDebouncedValue(q);
  const seq = useRef(0);
  const load = useCallback(() => {
    const s = ++seq.current;
    setLoading(true);
    setError(false);
    api.get("/admin/rfqs", { params: { status: status || undefined, q: dq || undefined, assigned: assigned || undefined, requirement_type: rtype || undefined } })
      .then((r) => { if (s === seq.current) setItems(r.data); })
      .catch(() => { if (s === seq.current) { setItems([]); setError(true); } })
      .finally(() => { if (s === seq.current) setLoading(false); });
  }, [status, dq, assigned, rtype]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((i) => i.id))));

  const bulk = async (action, value = "") => {
    if (action === "delete") {
      if (!window.confirm(`Delete ${selected.size} RFQ(s) and their quotes? RFQs with orders/invoices will be skipped — financial records are never bulk-deleted.`)) return;
    } else if (!window.confirm(`Apply "${action}${value ? `: ${value.replace(/_/g, " ")}` : ""}" to ${selected.size} RFQ(s)?`)) return;
    try {
      const { data } = await api.post("/admin/rfqs/bulk", { ids: [...selected], action, value });
      toast.success(`${data.processed} RFQ(s) ${action === "delete" ? "deleted" : "updated"}`);
      if (data.message) toast.warning(data.message);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk action failed"); }
  };

  return (
    <div data-testid="admin-rfq-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">RFQ Management</h1>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button data-testid="rfq-view-list" onClick={() => setView("list")}
            className={`px-3.5 py-2.5 text-[12.5px] font-[Poppins] font-semibold ${view === "list" ? "bg-[var(--pg-navy)] text-white" : "bg-white text-slate-500 hover:text-[var(--pg-navy)]"}`}>List</button>
          <button data-testid="rfq-view-report" onClick={() => setView("report")}
            className={`px-3.5 py-2.5 text-[12.5px] font-[Poppins] font-semibold inline-flex items-center gap-1.5 ${view === "report" ? "bg-[var(--pg-navy)] text-white" : "bg-white text-slate-500 hover:text-[var(--pg-navy)]"}`}><BarChart3 size={13} /> Report</button>
        </div>
      </div>
      {view === "report" ? <RFQReport /> : (
      <>
      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="rfq-search-input" className="pg-input !pl-9 !py-2.5 w-[260px]" placeholder="Search RFQ #, company, product..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="rfq-status-filter" className="pg-input !py-2.5 w-[220px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {RFQ_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="rfq-assignee-filter" className="pg-input !py-2.5 w-[200px]" value={assigned} onChange={(e) => setAssigned(e.target.value)}>
          <option value="">All assignees</option>
          {staff.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
        </select>
        <select data-testid="rfq-type-filter" className="pg-input !py-2.5 w-[240px]" value={rtype} onChange={(e) => setRtype(e.target.value)}>
          <option value="">All requirement types</option>
          {["Commercial Procurement", "Federal / Government Requirement", "Prime Contractor RFQ", "General Sourcing Request"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {selected.size > 0 && (
        <div data-testid="rfq-bulk-toolbar" className="mt-4 flex flex-wrap items-center gap-2.5 bg-[var(--pg-navy)] text-white rounded-xl px-4 py-3">
          <span className="text-[13px] font-[Poppins] font-semibold">{selected.size} selected</span>
          <select data-testid="bulk-status-select" className="!py-1.5 !px-2 text-[12px] rounded-lg text-[var(--pg-navy)] bg-white border-0" value="" onChange={(e) => e.target.value && bulk("status", e.target.value)}>
            <option value="">Set status…</option>
            {RFQ_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select data-testid="bulk-assign-select" className="!py-1.5 !px-2 text-[12px] rounded-lg text-[var(--pg-navy)] bg-white border-0" value="" onChange={(e) => bulk("assign", e.target.value === "__unassign__" ? "" : e.target.value)}>
            <option value="" disabled>Assign to…</option>
            <option value="__unassign__">(Unassign)</option>
            {staff.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
          </select>
          <select data-testid="bulk-priority-select" className="!py-1.5 !px-2 text-[12px] rounded-lg text-[var(--pg-navy)] bg-white border-0" value="" onChange={(e) => e.target.value && bulk("priority", e.target.value)}>
            <option value="">Set priority…</option>
            {RFQ_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button data-testid="bulk-delete-button" onClick={() => bulk("delete")}
            className="inline-flex items-center gap-1.5 text-[12px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600">
            <Trash2 size={13} /> Delete
          </button>
          <button data-testid="bulk-clear-button" onClick={() => setSelected(new Set())}
            className="ml-auto inline-flex items-center gap-1 text-[12px] text-white/70 hover:text-white">
            <X size={13} /> Clear
          </button>
        </div>
      )}

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13.5px] min-w-[860px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="pl-5 pr-1 py-3 w-[36px]">
                <input data-testid="rfq-select-all" type="checkbox" className="accent-[var(--pg-accent)] cursor-pointer"
                  checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} />
              </th>
              <th className="px-5 py-3">RFQ #</th><th className="px-5 py-3">Requested</th><th className="px-5 py-3">Company / Contact</th>
              <th className="px-5 py-3">Product / Service</th><th className="px-5 py-3">Priority</th><th className="px-5 py-3">Assignee</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Federal Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Loading...</td></tr>}
            {!loading && error && (
              <tr><td colSpan={8} className="px-5 py-8 text-center" data-testid="rfq-error-state">
                <span className="text-red-600">Failed to load RFQs.</span>
                <button data-testid="rfq-retry-button" onClick={load} className="ml-3 text-[var(--pg-accent)] font-[Poppins] font-semibold hover:underline">Retry</button>
              </td></tr>
            )}
            {!loading && !error && items.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400" data-testid="rfq-empty-state">No RFQs found.</td></tr>}
            {!loading && items.map((r) => (
              <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selected.has(r.id) ? "bg-blue-50/60" : ""}`}>
                <td className="pl-5 pr-1 py-3.5">
                  <input data-testid={`rfq-select-${r.rfq_number}`} type="checkbox" className="accent-[var(--pg-accent)] cursor-pointer"
                    checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                </td>
                <td className="px-5 py-3.5">
                  <Link data-testid={`rfq-row-${r.rfq_number}`} to={`/admin/rfqs/${r.id}`} className="font-[Poppins] font-semibold text-[var(--pg-accent)] hover:underline">{r.rfq_number}</Link>
                  {r.source === "reorder" && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[9.5px] font-[Poppins] font-bold bg-purple-100 text-purple-700 align-middle">REORDER</span>}
                </td>
                <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3.5">
                  <div className="font-medium text-[var(--pg-navy)]">{r.company || "—"}</div>
                  <div className="text-slate-500 text-[12.5px]">{r.first_name} {r.last_name}</div>
                </td>
                <td className="px-5 py-3.5 max-w-[240px] truncate">{r.product_service}</td>
                <td className="px-5 py-3.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${{ URGENT: "bg-red-100 text-red-700", HIGH: "bg-amber-100 text-amber-700", LOW: "bg-slate-100 text-slate-400" }[r.priority] || "bg-slate-100 text-slate-500"}`}>{r.priority || "NORMAL"}</span>
                </td>
                <td className="px-5 py-3.5 text-slate-500 text-[12.5px] max-w-[150px] truncate">{(staff.find((u) => u.email === r.assigned_to)?.name) || r.assigned_to || "—"}</td>
                <td className="px-5 py-3.5"><StatusBadge status={r.status} /></td>
                <td className="px-5 py-3.5" data-testid={`rfq-federal-status-${r.rfq_number}`}>
                  {r.requirement_type === "Federal / Government Requirement" ? (
                    <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${r.federal_status === "NOT_AWARDED" ? "bg-slate-200 text-slate-600" : r.federal_status === "DELIVERED" ? "bg-green-100 text-green-700" : "bg-[#e6f1fb] text-[var(--pg-navy)]"}`}>
                      {(r.federal_status || "RECEIVED").replace(/_/g, " ")}
                    </span>
                  ) : r.requirement_type && r.requirement_type !== "Commercial Procurement" ? (
                    <span className="text-[11px] text-slate-500">{r.requirement_type}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}
